import { BooleanField, DateField, Labeled, ReferenceField, TextField, useShowController } from "react-admin";
import { Grid, useMediaQuery } from "@mui/material";
import { settings } from "app";
import { SectionTitle } from "features/SectionTitle";
import { administrationRequests } from "api/administrationApi";
import { erasmusRequests } from "api/erasmusApi";
import ProfessorField from "server/components/Fields/ProfessorField";
import UserField from "server/components/Fields/UserField";
import { MobilityVisitType, ErasmusOutgoingEmployeeDocuments } from "utils/constants";
import DocumentFileLink from "components/Erasmus/Partials/DocumentFileLink";
import { normalizeTravelDocumentIds } from "components/Erasmus/Partials/TravelDocumentsInput";

const ShowForm = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const { record } = useShowController();

  const travelDocumentIds = normalizeTravelDocumentIds(record?.travelDocumentIds);

  return (
    <>
      <Grid
        container
        spacing={2}
        p={2}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Данни за кандидата"
            paddingY={0}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          xl={4}
        >
          <Labeled label="Служител">
            <ProfessorField source="employeeId" />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          xl={4}
        >
          <Labeled label="Университет">
            <TextField
              source="universityName"
              emptyText="-"
            />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          xl={4}
        >
          <Labeled label="Тип визита">
            <ReferenceField
              source="mobilityVisitType"
              reference={erasmusRequests.getMobilityTypesSelect()}
            >
              <TextField
                source="nameBg"
                emptyText="-"
              />
            </ReferenceField>
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          xl={4}
        >
          <Labeled label="Структурна единица">
            <ReferenceField
              source="structuralUnitId"
              reference={administrationRequests.getStructuralUnitsSelect()}
            >
              <TextField
                source="name"
                emptyText="-"
              />
            </ReferenceField>
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          xl={4}
        >
          <Labeled label="Начална дата">
            <DateField
              source="startDate"
              locales="bg-BG"
              emptyText="-"
            />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          xl={4}
        >
          <Labeled label="Крайна дата">
            <DateField
              source="endDate"
              locales="bg-BG"
              emptyText="-"
            />
          </Labeled>
        </Grid>

        {record?.mobilityVisitType === MobilityVisitType.BIP && (
          <>
            <Grid
              item
              xs={12}
              md={6}
              xl={4}
            >
              <Labeled label="BIP имe">
                <TextField
                  source="bipName"
                  emptyText="-"
                />
              </Labeled>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              xl={4}
            >
              <Labeled label="BIP номер">
                <TextField
                  source="bipNumber"
                  emptyText="-"
                />
              </Labeled>
            </Grid>
          </>
        )}

        <Grid
          item
          xs={12}
          md={6}
          xl={4}
        >
          <Labeled label="Зелен транспорт">
            <BooleanField
              source="isGreenTransport"
              sx={{ width: "fit-content" }}
            />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          xl={4}
        >
          <Labeled label="Споделен транспорт">
            <BooleanField
              source="isSharedTransport"
              sx={{ width: "fit-content" }}
            />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={6}
          xl={4}
        >
          <Labeled label="Приключил">
            <BooleanField
              source="isCompleted"
              sx={{ width: "fit-content" }}
            />
          </Labeled>
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
        p={2}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Документи"
            paddingY={0}
          />
        </Grid>

        {ErasmusOutgoingEmployeeDocuments.map(({ source, label }) =>
          record?.[source] ? (
            <Grid
              key={source}
              item
              xs={12}
              md={6}
            >
              <Labeled label={label}>
                <DocumentFileLink
                  id={record[source]}
                  documentName={label}
                />
              </Labeled>
            </Grid>
          ) : null,
        )}

        {travelDocumentIds.length > 0 && (
          <>
            <Grid
              item
              xs={12}
            >
              <SectionTitle
                title="Документи за пътуване"
                paddingY={0}
              />
            </Grid>

            {travelDocumentIds.map(id => (
              <Grid
                key={id}
                item
                xs={12}
                md={6}
              >
                <DocumentFileLink
                  id={id}
                  documentName="Документ за пътуване"
                />
              </Grid>
            ))}
          </>
        )}
      </Grid>

      <Grid
        container
        spacing={2}
        p={2}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <SectionTitle
            title="Одит"
            paddingY={0}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Създал">
            <UserField source="createdByUserId" />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Създадено на">
            <DateField
              source="createdOn"
              locales="bg-BG"
              showTime
              emptyText="-"
            />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Променил">
            <UserField source="updatedByUserId" />
          </Labeled>
        </Grid>

        <Grid
          item
          xs={12}
          md={3}
        >
          <Labeled label="Променено на">
            <DateField
              source="updatedOn"
              locales="bg-BG"
              showTime
              emptyText="-"
            />
          </Labeled>
        </Grid>
      </Grid>
    </>
  );
};

export default ShowForm;
